// VenueBoards Typography System
export const fonts = {
  // Font families
  primary:
    "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
  secondary:
    "'Source Sans Pro', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  monospace: "'JetBrains Mono', 'Fira Code', 'Consolas', monospace",

  // Font sizes (rem units for scalability)
  sizes: {
    xs: "0.75rem", // 12px
    sm: "0.875rem", // 14px
    md: "1rem", // 16px (base)
    lg: "1.125rem", // 18px
    xl: "1.25rem", // 20px
    xxl: "1.5rem", // 24px

    // Heading sizes
    h1: "2.5rem", // 40px
    h2: "2rem", // 32px
    h3: "1.75rem", // 28px
    h4: "1.5rem", // 24px
    h5: "1.25rem", // 20px
    h6: "1.125rem", // 18px

    // TV Display sizes (larger for readability)
    tvSmall: "1.5rem", // 24px
    tvMedium: "2rem", // 32px
    tvLarge: "3rem", // 48px
    tvXLarge: "4rem", // 64px
    tvDisplay: "6rem", // 96px (for time/large text)
  },

  // Font weights
  weights: {
    light: 300,
    normal: 400,
    medium: 500,
    semibold: 600,
    bold: 700,
    extrabold: 800,
  },

  // Line heights
  lineHeights: {
    tight: 1.25,
    normal: 1.5,
    relaxed: 1.625,
    loose: 2,
  },
} as const;

// Text styles for common use cases
export const textStyles = {
  // Body text
  bodySmall: {
    fontSize: fonts.sizes.sm,
    lineHeight: fonts.lineHeights.normal,
    fontWeight: fonts.weights.normal,
  },

  bodyDefault: {
    fontSize: fonts.sizes.md,
    lineHeight: fonts.lineHeights.normal,
    fontWeight: fonts.weights.normal,
  },

  bodyLarge: {
    fontSize: fonts.sizes.lg,
    lineHeight: fonts.lineHeights.normal,
    fontWeight: fonts.weights.normal,
  },

  // Labels and UI text
  label: {
    fontSize: fonts.sizes.sm,
    lineHeight: fonts.lineHeights.tight,
    fontWeight: fonts.weights.medium,
  },

  caption: {
    fontSize: fonts.sizes.xs,
    lineHeight: fonts.lineHeights.normal,
    fontWeight: fonts.weights.normal,
  },

  // Button text
  button: {
    fontSize: fonts.sizes.md,
    lineHeight: fonts.lineHeights.tight,
    fontWeight: fonts.weights.medium,
  },

  buttonSmall: {
    fontSize: fonts.sizes.sm,
    lineHeight: fonts.lineHeights.tight,
    fontWeight: fonts.weights.medium,
  },

  // Headings
  heading1: {
    fontSize: fonts.sizes.h1,
    lineHeight: fonts.lineHeights.tight,
    fontWeight: fonts.weights.bold,
  },

  heading2: {
    fontSize: fonts.sizes.h2,
    lineHeight: fonts.lineHeights.tight,
    fontWeight: fonts.weights.semibold,
  },

  heading3: {
    fontSize: fonts.sizes.h3,
    lineHeight: fonts.lineHeights.tight,
    fontWeight: fonts.weights.semibold,
  },

  // TV Display specific styles
  tvEventTitle: {
    fontSize: fonts.sizes.tvLarge,
    lineHeight: fonts.lineHeights.tight,
    fontWeight: fonts.weights.bold,
  },

  tvTime: {
    fontSize: fonts.sizes.tvDisplay,
    lineHeight: fonts.lineHeights.tight,
    fontWeight: fonts.weights.bold,
    fontFamily: fonts.monospace,
  },

  tvRoomName: {
    fontSize: fonts.sizes.tvMedium,
    lineHeight: fonts.lineHeights.tight,
    fontWeight: fonts.weights.semibold,
  },

  tvEventDetails: {
    fontSize: fonts.sizes.tvSmall,
    lineHeight: fonts.lineHeights.normal,
    fontWeight: fonts.weights.normal,
  },
} as const;
